import type { VcsStatusResult } from "@afar-research/contracts";
import type { ClientReadiness } from "@afar-research/client-runtime/connection";

import { resolveCompactAction, type GitCompactAction } from "./GitActionsControl.logic";

export type EnvironmentPanelAvailability = "loading" | "unavailable" | "non-repository" | "ready";

export type EnvironmentPanelAction =
  | GitCompactAction
  | {
      readonly kind: "init";
      readonly label: string;
      readonly disabled: boolean;
    };

export interface EnvironmentPanelModel {
  readonly availability: EnvironmentPanelAvailability;
  readonly changes: {
    readonly insertions: number | null;
    readonly deletions: number | null;
    readonly label: string;
  };
  readonly environmentLabel: string;
  readonly workspaceLabel: string;
  readonly branchLabel: string;
  readonly action: EnvironmentPanelAction;
  readonly canCompare: boolean;
  readonly unavailableReason: string | null;
}

export function resolveEnvironmentWorkspaceLabel(worktreePath: string | null): string {
  if (worktreePath === null) {
    return "Current checkout";
  }
  const name = /[^\\/]+(?=[\\/]*$)/.exec(worktreePath)?.[0];
  return name ? `Worktree · ${name}` : "Worktree";
}

export function resolveEnvironmentPanelModel(input: {
  readonly connectionPhase: ClientReadiness;
  readonly environmentLabel: string | null;
  readonly workspaceLabel: string;
  readonly gitStatus: VcsStatusResult | null;
  readonly gitStatusError: string | null;
  readonly isStatusPending: boolean;
  readonly isBusy: boolean;
}): EnvironmentPanelModel {
  const environmentLabel = input.environmentLabel ?? "Unknown environment";
  if (input.connectionPhase !== "ready") {
    const isUnavailable = input.connectionPhase === "unavailable";
    return {
      availability: isUnavailable ? "unavailable" : "loading",
      changes: {
        insertions: null,
        deletions: null,
        label: isUnavailable ? "Unavailable" : "Checking status...",
      },
      environmentLabel,
      workspaceLabel: input.workspaceLabel,
      branchLabel: isUnavailable ? "Unavailable" : "Checking status...",
      action: {
        kind: "none",
        label: isUnavailable ? "Unavailable" : "Checking status...",
        disabled: true,
      },
      canCompare: false,
      unavailableReason: isUnavailable ? "Environment is unavailable." : null,
    };
  }

  const gitStatus = input.gitStatus;
  // Live Atom streams remain `waiting` after emitting a value, until the subscription ends.
  // Treat pending as initial loading only when no repository snapshot is available yet.
  if (input.gitStatusError !== null || gitStatus === null) {
    const isUnavailable = input.gitStatusError !== null || !input.isStatusPending;
    const label = isUnavailable ? "Unavailable" : "Checking status...";
    return {
      availability: isUnavailable ? "unavailable" : "loading",
      changes: { insertions: null, deletions: null, label },
      environmentLabel,
      workspaceLabel: input.workspaceLabel,
      branchLabel: label,
      action: { kind: "none", label, disabled: true },
      canCompare: false,
      unavailableReason:
        input.gitStatusError ?? (isUnavailable ? "Repository status is unavailable." : null),
    };
  }

  if (!gitStatus.isRepo) {
    return {
      availability: "non-repository",
      changes: { insertions: null, deletions: null, label: "Not a repository" },
      environmentLabel,
      workspaceLabel: input.workspaceLabel,
      branchLabel: "Not a repository",
      action: {
        kind: "init",
        label: input.isBusy ? "Initializing Git..." : "Initialize Git",
        disabled: input.isBusy,
      },
      canCompare: false,
      unavailableReason: null,
    };
  }

  const action = resolveCompactAction(gitStatus, input.isBusy);

  return {
    availability: "ready",
    changes: {
      insertions: gitStatus.workingTree.insertions,
      deletions: gitStatus.workingTree.deletions,
      label: `+${gitStatus.workingTree.insertions} -${gitStatus.workingTree.deletions}`,
    },
    environmentLabel,
    workspaceLabel: input.workspaceLabel,
    branchLabel: gitStatus.refName ?? "Detached HEAD",
    action,
    canCompare: true,
    unavailableReason: null,
  };
}
